import { get, isNil } from 'lodash-es'
import { parsePrimaryKeyAndSource, compareValuesNullsLast } from '@/components/shared-table/utils'

import { WEBTEST_URL } from '@/main'
import {
  buildColumnDef,
  isPredictedField,
  isExperimentalField,
  getDefaultCellClass,
  getFormattedValue,
  formatValueWithConversion,
  getModelPredictionRoot,
} from '@/components/shared-table/buildColumnDef'
import { getChemicalMassFromParams } from '@/components/shared-table/utils'

const decryptEndpointAbbreviation = (abbreviation) => {
  const mapping = {
    A: 'Active',
    N: 'Not active',
    I: 'Inconclusive',
  }
  return get(mapping, abbreviation, abbreviation)
}

const getReportUrl = (link) => `${WEBTEST_URL}${link}`

const calculateAggregateValueBySource = (sourceEntries) => {
  if (!sourceEntries?.length) return null

  const isPredictedEntries = () => sourceEntries.some((entry) => entry && 'method' in entry)

  if (!isPredictedEntries()) {
    return sourceEntries.map((entry) => entry?.value).join(', ')
  }

  const anyValue = sourceEntries.find((entry) => entry && !isNil(entry.value))?.value
  return anyValue ?? null
}

const getPredictionRawValue = (predictionContext) => {
  const { sourceEntries = [], displayedEndpointString } = predictionContext || {}

  const consensusValue = sourceEntries.find((entry) => entry && entry.method === 'consensus')?.value
  if (!isNil(consensusValue)) return consensusValue

  const hasAnyNumericValueInSourceEntries = () => sourceEntries.some((entry) => entry && 'value' in entry)

  if (!hasAnyNumericValueInSourceEntries()) {
    return displayedEndpointString
  }

  return calculateAggregateValueBySource(sourceEntries)
}

const findPredictionContextByFieldKey = ({ modelId, predictionsByModelId, fieldKey }) => {
  const predictionsForModel = getModelPredictionRoot(predictionsByModelId, modelId)

  if (!predictionsForModel?.length) return null

  const { primaryKey: endpointId, sourceType } = parsePrimaryKeyAndSource(fieldKey, modelId)

  const endpointResult = predictionsForModel.find((prediction) => prediction?.endpoint?.id === endpointId)

  const sourceEntries = get(endpointResult, sourceType, [])

  const getStringDisplayValueBySourceType = () => {
    const valueKey =
      sourceType === 'experimental'
        ? 'displayedExperimentalString'
        : sourceType === 'predicted'
          ? 'displayedPredictedString'
          : null
    return get(endpointResult, valueKey, null)
  }

  return {
    endpointResult,
    displayedEndpointString: getStringDisplayValueBySourceType(),
    sourceEntries,
    link: sourceType === 'predicted' && endpointResult?.path ? getReportUrl(endpointResult.path) : null,
  }
}

const buildWebtestColumnDef = ({ column, visibleColumnIds, tableSettings }) => {
  return buildColumnDef({
    column,
    visibleColumnIds,
    tableSettings,

    minWidth: 80,

    ...(isPredictedField(column?.id)
      ? {
          tooltipComponent: 'WebtestTooltip',

          tooltipComponentParams: (params) => ({
            prediction: findPredictionContextByFieldKey({
              modelId: column?.modelId,
              predictionsByModelId: params?.data?.predictionsByModelId,
              fieldKey: params?.colDef?.field,
            })?.endpointResult,
          }),

          onCellClicked: (params) => {
            const hasPrediction = !!getPredictionRawValue(params?.value)

            const link = params?.value?.link
            if (!link || !hasPrediction) return

            window.open(link, '_blank', 'noopener,noreferrer')
          },
        }
      : {}),

    cellClass: (params) => {
      const isPredicted = isPredictedField(column?.id)
      const isExperimental = isExperimentalField(column?.id)
      const link = params?.value?.link

      const hasPrediction = !!getPredictionRawValue(params?.value)

      const constructedCustomCellClass = () => {
        const inconclusiveClass = params?.value?.displayedEndpointString === 'I' ? 'text-warning' : null
        // const tooltipClass = isPredicted && hasPrediction ? 'tooltip-cell' : ''
        return [inconclusiveClass].filter(Boolean)
      }

      return getDefaultCellClass({
        isLinkCell: isPredicted && hasPrediction && !!link,
        isExperimental,
        customClasses: constructedCustomCellClass(),
        params,
        column,
      })
    },

    tooltipValueGetter: (params) => decryptEndpointAbbreviation(params?.valueFormatted),

    valueGetter: (params) =>
      findPredictionContextByFieldKey({
        modelId: column?.modelId,
        predictionsByModelId: params?.data?.predictionsByModelId,
        fieldKey: params?.colDef?.field,
      }),

    valueFormatter: (params) =>
      getFormattedValue({
        params,
        column,
        formatFunction: () => {
          const rawValue = getPredictionRawValue(params?.value)
          return formatValueWithConversion({
            value: rawValue,
            fromUnit: column?.originalUnit,
            toUnit: column?.unit,
            mass: getChemicalMassFromParams(params),
          })
        },
      }),

    comparator: (aValue, bValue, _nodeA, _nodeB, isDescending) => {
      const rawAValue = getPredictionRawValue(aValue)
      const rawBValue = getPredictionRawValue(bValue)
      return compareValuesNullsLast(rawAValue, rawBValue, isDescending)
    },
  })
}

export { buildWebtestColumnDef, decryptEndpointAbbreviation }
